<script setup lang="ts">
// The WeAreDevelopers mark, copied from the attendee app's header so both apps
// look like they come from the same place. Deliberately not a link: there is
// nowhere else in this app to navigate to.
//
// The logo also carries the hidden maintenance entry. Keeping the gesture and the
// modal in this one component means it works on every screen showing the logo,
// rather than each screen wiring it up again.
const showUnlock = ref(false)
const showPanel = ref(false)

const { pressing, handlers } = useLongPress(() => {
  showUnlock.value = true
})

const onUnlocked = () => {
  showUnlock.value = false
  showPanel.value = true
}
</script>

<template>
  <span class="wad-logo" :class="{ 'wad-logo--pressing': pressing }" v-on="handlers">
    <svg
      class="wad-logo__mark"
      width="56"
      height="17"
      viewBox="0 0 56 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="WeAreDevelopers"
    >
      <path
        d="M30.3623 0.000103207H26.2731L21.7545 11.699L17.236 0.000103207H13.1468L8.60776 11.699L4.0892 0.000103207H0L6.56316 17H10.6524L15.1914 5.30115L19.7099 17H23.7991L28.3177 5.30115L32.8363 17H36.9459L30.3623 0.000103207Z"
        fill="var(--wad4-c-text-text-brand-wad)"
      />
      <path
        d="M38.9889 0V4.04179L50.7045 8.48979L38.9889 12.9581V16.9999L56 10.5208V6.47905L38.9889 0Z"
        fill="var(--wad4-c-text-text-brand-wad)"
      />
    </svg>

    <!-- Teleported because this span sets touch-action: none for the long press, and
         WebKit applies an ancestor's touch-action to a descendant's scroller, so a
         panel rendered inside the span cannot be scrolled by touch. Touches on the
         overlays also must not feed the span's press handlers. -->
    <Teleport to="body">
      <MaintenanceUnlockModal
        v-if="showUnlock"
        @close="showUnlock = false"
        @unlocked="onUnlocked"
      />

      <MaintenancePanel v-if="showPanel" @close="showPanel = false" />
    </Teleport>
  </span>
</template>

<style lang="scss" scoped>
.wad-logo {
  display: inline-flex;
  flex-shrink: 0;
  // Widens the press area beyond the mark itself, which is only 17px tall.
  padding: $wad4-spacing-2;
  // Stops iOS treating the long press as a drag of the image.
  -webkit-touch-callout: none;
  // Without this a press that starts on the logo is taken as a page gesture and the
  // pointer events stop arriving partway through.
  touch-action: none;
  transition: opacity 0.6s ease;

  // Shows the press is registering. The gesture is hidden by design, but someone who
  // knows about it should not have to guess whether it is working.
  &--pressing {
    opacity: 0.45;
  }

  &__mark {
    display: block;
  }
}
</style>
